"use client"

import { useState, useEffect, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "framer-motion"
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AccessTime as TimeIcon,
} from "@mui/icons-material"
import { toast } from "react-toastify"
import apiClient from "../utils/apiClient"

const AdminSlotManagement = () => {
  const navigate = useNavigate()
  const [slots, setSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [editDialog, setEditDialog] = useState({
    open: false,
    slot: null,
    isNew: false,
  })
  const [dayStatus, setDayStatus] = useState("open") // "open", "closed"
  const [slotType, setSlotType] = useState("default")
  const [dayOfWeek, setDayOfWeek] = useState(0)
  const [extendDialog, setExtendDialog] = useState({
    open: false,
    slot: null,
    newEndTime: "",
  })

  // Predefined slot options
  const PREDEFINED_SLOTS = [
    {
      value: "Breakfast",
      label: "Breakfast",
      emoji: "",
      startTime: dayOfWeek === 6 ? "09:00" : "08:00", // Saturday: 9:00, Others: 8:00
      endTime: dayOfWeek === 6 ? "09:30" : "08:40", // Saturday: 9:30, Others: 8:40
    },
    {
      value: "Lunch",
      label: "Lunch",
      emoji: "",
      startTime: dayOfWeek === 6 ? "12:00" : "11:00", // Saturday: 12:00, Others: 11:00
      endTime: dayOfWeek === 6 ? "12:30" : "11:40", // Saturday: 12:30, Others: 11:40
    },
    {
      value: "Dinner",
      label: "Dinner",
      emoji: "",
      startTime: "19:00",
      endTime: "19:40",
    },
    {
      value: "Custom",
      label: "Custom Slot",
      emoji: "",
      startTime: "",
      endTime: "",
    },
  ]

  const isPredefinedSlot = (slotName) => {
    return true
  }

  const getPredefinedSlotTimes = (slotName) => {
    const slot = PREDEFINED_SLOTS.find((slot) => slot.value === slotName)
    return slot ? { startTime: slot.startTime, endTime: slot.endTime } : null
  }

  // Get today's date info
  const today = new Date()
  const dayName = today.toLocaleDateString("en-US", { weekday: "long" })
  const dateString = today.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  })
  const isSunday = today.getDay() === 0

  // Static data for now (replace with API call later)
  const fetchTodaySlots = useCallback(async () => {
    setLoading(true)
    try {
      const response = await apiClient.get(`/admin/slots`)

      const slotData = response.data.slot

      // Transform the API response to our component's format
      if (slotData) {
        const formattedSlots = slotData.slots.map((slot) => ({
          id: slot.id,
          name: slot.name,
          startTime: slot.startTime,
          endTime: slot.endTime,
          restaurants: slot.restaurants || [],
          foodItems: slot.foodItems || [],
          item_options: slot.item_options || [],
          itemOptionsConfig: slot.itemOptionsConfig || [],
          isActive: slotData.is_active,
        }))

        setSlots(formattedSlots)
        setDayStatus(slotData.is_active ? "open" : "closed")

        // Store the original slot type for reference when saving
        setSlotType(slotData.type)
        setDayOfWeek(slotData.dayOfWeek)
      } else {
        setSlots([])
        setDayStatus("closed")
      }
    } catch (error) {
      console.error("Error fetching slots:", error)

      toast.error("Failed to fetch today's slots")
      setSlots([])
      setDayStatus("closed")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchTodaySlots()
  }, [fetchTodaySlots])

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchTodaySlots()
    setTimeout(() => setRefreshing(false), 500)
  }

  const handleSaveSlots = async () => {
    setSaving(true)
    try {
      // Format slots for the API
      const formattedSlots = slots.map((slot) => ({
        id: slot.id,
        name: slot.name,
        startTime: slot.startTime,
        endTime: slot.endTime,
        restaurants: slot.restaurants,
        foodItems: slot.foodItems || [],
        item_options: slot.item_options || [],
        itemOptionsConfig: slot.itemOptionsConfig || [],
      }))

      // Prepare request body
      const requestBody = {
        slots: formattedSlots,
        is_active: dayStatus === "open",
      }

      await apiClient.patch(`/admin/slots`, requestBody)

      toast.success("Slots saved successfully!")

      // Refresh slots after saving
      await fetchTodaySlots()
    } catch (error) {
      console.error("Error saving slots:", error)

      toast.error("Failed to save slots")
    } finally {
      setSaving(false)
    }
  }

  const handleAddSlot = () => {
    setEditDialog({
      open: true,
      slot: {
        id: Date.now(),
        name: "",
        startTime: "",
        endTime: "",
        restaurants: [],
        foodItems: [],
        item_options: [],
        itemOptionsConfig: [],
        isActive: true,
      },
      isNew: true,
    })
  }

  const handleEditSlot = (slot) => {
    // Check if this is a custom slot (not in predefined list)
    const isCustomSlot = !PREDEFINED_SLOTS.find(p => p.value === slot.name);
    
    setEditDialog({
      open: true,
      slot: {
        ...slot,
        name: isCustomSlot ? "Custom" : slot.name,
        customName: isCustomSlot ? slot.name : ""
      },
      isNew: false,
    })
  }

  const handleDeleteSlot = (slotId) => {
    setSlots(slots.filter((slot) => slot.id !== slotId))
    toast.success("Slot deleted successfully. Click \"Save Changes\" to apply.")
  }

  const handleSaveSlot = () => {
    const { slot, isNew } = editDialog

    if (!slot.name || !slot.startTime || !slot.endTime) {
      toast.error("Please fill in slot name, start time, and end time")
      return
    }

    if (slot.startTime >= slot.endTime) {
      toast.error("End time must be after start time")
      return
    }

    // Validate at least one item option is selected
    if (!slot.item_options || slot.item_options.length === 0) {
      toast.error("Please add at least one item option")
      return
    }

    // Use custom name if Custom slot is selected
    const finalSlotName = slot.name === "Custom" ? slot.customName : slot.name;
    
    if (slot.name === "Custom" && !slot.customName?.trim()) {
      toast.error("Please enter a custom slot name");
      return;
    }

    // Check for duplicate slot names
    const existingSlot = slots.find(s => s.name === finalSlotName && s.id !== slot.id);
    if (existingSlot) {
      toast.error(`A slot with the name "${finalSlotName}" already exists`);
      return;
    }

    // Ensure foodItems is always an array
    const updatedSlot = {
      ...slot,
      name: finalSlotName,
      foodItems: slot.foodItems || [],
      item_options: slot.item_options || [],
      itemOptionsConfig: slot.itemOptionsConfig || [],
    }

    if (isNew) {
      setSlots([...slots, updatedSlot])
      toast.success("New slot added successfully. Click \"Save Changes\" to apply.")
    } else {
      setSlots(slots.map((s) => (s.id === updatedSlot.id ? updatedSlot : s)))
      toast.success("Slot updated successfully. Click \"Save Changes\" to apply.")
    }

    setEditDialog({ open: false, slot: null, isNew: false })
  }

  const handleAddItemOption = (option) => {
    const currentOptions = editDialog.slot?.item_options || []
    if (!currentOptions.includes(option)) {
      setEditDialog({
        ...editDialog,
        slot: {
          ...editDialog.slot,
          item_options: [...currentOptions, option],
        },
      })
    }
  }

  const handleRemoveItemOption = (optionToRemove) => {
    const currentOptions = editDialog.slot?.item_options || []
    setEditDialog({
      ...editDialog,
      slot: {
        ...editDialog.slot,
        item_options: currentOptions.filter((option) => option !== optionToRemove),
      },
    })
  }

  const handleAddCustomOption = (customOption) => {
    if (customOption.trim()) {
      handleAddItemOption(customOption.trim())
    }
  }

  const handleMarkDayClosed = () => {
    setDayStatus("closed")
    setSlots([])
    toast.success("Day marked as closed. Click \"Save Changes\" to apply.")
  }

  const handleExtendSlot = (slot) => {
    setExtendDialog({
      open: true,
      slot: { ...slot },
      newEndTime: slot.endTime,
    })
  }

  const handleSaveExtension = () => {
    if (!extendDialog.newEndTime) {
      toast.error("Please select a new end time")
      return
    }

    // Get original predefined end time for this slot type
    const slotName = extendDialog.slot.name
    const predefinedSlot = PREDEFINED_SLOTS.find(s => s.value === slotName)
    const originalPredefinedEndTime = predefinedSlot ? predefinedSlot.endTime : extendDialog.slot.endTime
    
    const newEndTime = extendDialog.newEndTime
    
    // Convert times to minutes for comparison
    const originalMinutes = parseInt(originalPredefinedEndTime.split(':')[0]) * 60 + parseInt(originalPredefinedEndTime.split(':')[1])
    const newMinutes = parseInt(newEndTime.split(':')[0]) * 60 + parseInt(newEndTime.split(':')[1])
    
    if (newMinutes <= originalMinutes) {
      toast.error(`New end time must be after the original end time (${originalPredefinedEndTime})`)
      return
    }
    
    const extensionMinutes = newMinutes - originalMinutes
    
    // Check if extension is in 10-minute increments
    if (extensionMinutes % 10 !== 0) {
      toast.error("Extension must be in 10-minute increments")
      return
    }
    
    // Check if extension exceeds 30 minutes from original predefined time
    if (extensionMinutes > 30) {
      toast.error(`Maximum extension allowed is 30 minutes from original time (${originalPredefinedEndTime})`)
      return
    }

    const updatedSlots = slots.map(slot => 
      slot.id === extendDialog.slot.id 
        ? { ...slot, endTime: extendDialog.newEndTime }
        : slot
    )
    
    setSlots(updatedSlots)
    toast.success(`Slot "${extendDialog.slot.name}" extended by ${extensionMinutes} minutes to ${extendDialog.newEndTime}. Click "Save Changes" to apply.`)
    setExtendDialog({ open: false, slot: null, newEndTime: "" })
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  }

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
          <CircularProgress size={60} sx={{ color: "#20d5d5" }} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ pb: { xs: 10, sm: 4 } }}>
      <motion.div variants={containerVariants} initial="hidden" animate="visible">
        {/* Enhanced Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 4,
              p: 4,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 50%, #3d566e 100%)",
              borderRadius: 4,
              color: "white",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "radial-gradient(circle at 80% 20%, rgba(255,255,255,0.15) 0%, transparent 50%)",
                pointerEvents: "none",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                top: "-50%",
                left: "-50%",
                width: "200%",
                height: "200%",
                background: "conic-gradient(from 180deg, transparent, rgba(255,255,255,0.08), transparent)",
                animation: "rotate 25s linear infinite",
                pointerEvents: "none",
              },
            }}
          >
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <motion.div
                    animate={{
                      rotate: [0, 15, -15, 0],
                      scale: [1, 1.1, 1],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: "easeInOut",
                    }}
                  >
                    <ScheduleIcon sx={{ fontSize: 48 }} />
                  </motion.div>
                  <Box>
                    <Typography
                      variant="h3"
                      fontWeight={800}
                      sx={{
                        background: "linear-gradient(45deg, #fff 30%, #ffe0b2 90%)",
                        backgroundClip: "text",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        mb: 1,
                      }}
                    >
                      Slot Management
                    </Typography>
                    <Typography variant="h6" sx={{ opacity: 0.9 }}>
                      {dayName}, {dateString}
                    </Typography>
                  </Box>
                </Box>
                <Tooltip title="Refresh Slots">
                  <IconButton
                    onClick={handleRefresh}
                    disabled={refreshing}
                    sx={{
                      color: "white",
                      position: "relative",
                      zIndex: 1,
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                  >
                    <motion.div
                      animate={refreshing ? { rotate: 360 } : {}}
                      transition={{ duration: 1, ease: "linear" }}
                    >
                      <RefreshIcon />
                    </motion.div>
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Action Buttons */}
        <motion.div variants={itemVariants}>
          <Paper
            sx={{
              p: 3,
              mb: 4,
              background: "linear-gradient(135deg, rgba(32, 213, 213, 0.05) 0%, rgba(6, 214, 160, 0.05) 100%)",
              border: "2px solid rgba(32, 213, 213, 0.2)",
              borderRadius: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 3 }}>
              <Button
                variant="contained"
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                  color: "white",
                }}
              >
                Today's Slots
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/admin/default-slots')}
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  borderColor: "#20d5d5",
                  color: "#20d5d5",
                }}
              >
                Templates
              </Button>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              {dayStatus === "open" && (
                <>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSlot}
                      sx={{
                        background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                        color: "white",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: "0 4px 15px rgba(6, 214, 160, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(6, 214, 160, 0.4)",
                        },
                      }}
                    >
                      Add Slot
                    </Button>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outlined"
                      startIcon={<BlockIcon />}
                      onClick={handleMarkDayClosed}
                      sx={{
                        borderColor: "#f44336",
                        color: "#f44336",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "#f44336",
                          backgroundColor: "rgba(244, 67, 54, 0.1)",
                        },
                      }}
                    >
                      Mark Today as Closed
                    </Button>
                  </motion.div>

                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outlined"
                      startIcon={<TimeIcon />}
                      onClick={() => navigate('/admin/emergency-orders')}
                      sx={{
                        borderColor: "#ff9800",
                        color: "#ff9800",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "#ff9800",
                          backgroundColor: "rgba(255, 152, 0, 0.1)",
                        },
                      }}
                    >
                      Emergency Orders
                    </Button>
                  </motion.div>



                  {/* <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="outlined"
                      startIcon={<ResetIcon />}
                      onClick={handleResetToDefault}
                      sx={{
                        borderColor: "#20d5d5",
                        color: "#20d5d5",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "#20d5d5",
                          backgroundColor: "rgba(32, 213, 213, 0.1)",
                        },
                      }}
                    >
                      Reset to Default
                    </Button>
                  </motion.div> */}
                </>
              )}

              {dayStatus === "closed" && (
                <>
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      onClick={() => {
                        setDayStatus("open")
                        toast.success("Day reopened for slot management. Click \"Save Changes\" to apply.")
                      }}
                      sx={{
                        background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                        color: "white",
                        fontWeight: 700,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: "0 4px 15px rgba(6, 214, 160, 0.3)",
                        "&:hover": {
                          boxShadow: "0 6px 20px rgba(6, 214, 160, 0.4)",
                        },
                      }}
                    >
                      {isSunday ? "Open Sunday and Add Slots" : "Reopen Day and Add Slots"}
                    </Button>
                  </motion.div>
                </>
              )}

              <Box sx={{ ml: "auto" }}>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                    onClick={handleSaveSlots}
                    disabled={saving}
                    sx={{
                      background: "linear-gradient(135deg, #20d5d5 0%, #06d6a0 100%)",
                      color: "white",
                      fontWeight: 700,
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      boxShadow: "0 4px 15px rgba(32, 213, 213, 0.3)",
                      "&:hover": {
                        boxShadow: "0 6px 20px rgba(32, 213, 213, 0.4)",
                      },
                      "&:disabled": {
                        background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                        color: "#94a3b8",
                        boxShadow: "none",
                      },
                    }}
                  >
                    {saving ? "Saving..." : "✓ Save Changes"}
                  </Button>
                </motion.div>
              </Box>
            </Box>
          </Paper>
        </motion.div>

        {/* Slots Display */}
        <motion.div variants={itemVariants}>
          <Typography variant="h5" fontWeight={600} sx={{ mb: 3, color: "text.primary" }}>
            Today's Slots
          </Typography>

          <AnimatePresence>
            {dayStatus === "closed" ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Paper
                  sx={{
                    p: 6,
                    textAlign: "center",
                    background: "linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)",
                    border: "2px dashed #f44336",
                    borderRadius: 4,
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  >
                    <BlockIcon sx={{ fontSize: 64, color: "#f44336", mb: 2 }} />
                  </motion.div>
                  <Typography variant="h4" fontWeight={800} color="#f44336" gutterBottom>
                    This Day is Closed
                  </Typography>
                  <Typography variant="h6" color="#f44336">
                    {isSunday ? "Sunday is closed by default" : "No slots available today"}
                  </Typography>
                </Paper>
              </motion.div>
            ) : slots.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
              >
                <Paper
                  sx={{
                    p: 6,
                    textAlign: "center",
                    background: "linear-gradient(135deg, #f9f9f9 0%, #e9ecef 100%)",
                    border: "2px dashed #dee2e6",
                    borderRadius: 4,
                  }}
                >
                  <motion.div
                    animate={{ y: [0, -10, 0] }}
                    transition={{ duration: 2, repeat: Number.POSITIVE_INFINITY }}
                  >
                    <ScheduleIcon sx={{ fontSize: 64, color: "#64748b", mb: 2 }} />
                  </motion.div>
                  <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
                    No Slots Configured
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                    Add your first slot to get started
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddSlot}
                    sx={{
                      background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                      fontWeight: 700,
                    }}
                  >
                    Add First Slot
                  </Button>
                </Paper>
              </motion.div>
            ) : (
              <Grid container spacing={3}>
                {slots.map((slot, index) => (
                  <Grid item xs={12} md={6} lg={4} key={slot.id}>
                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -8 }}
                    >
                      <Card
                        sx={{
                          height: "100%",
                          background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
                          border: "1px solid #e2e8f0",
                          overflow: "hidden",
                          position: "relative",
                        }}
                      >
                        {/* Card Header */}
                        <Box
                          sx={{
                            background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
                            p: 2,
                            color: "white",
                          }}
                        >
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Typography variant="h6" fontWeight={600}>
                              {slot.name}
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Tooltip title="Edit Slot">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSlot(slot)}
                                  sx={{
                                    color: "white",
                                    backgroundColor: "rgba(255,255,255,0.1)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255,255,255,0.2)",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Extend Slot">
                                <IconButton
                                  size="small"
                                  onClick={() => handleExtendSlot(slot)}
                                  sx={{
                                    color: "#ff9800",
                                    backgroundColor: "rgba(255, 152, 0, 0.1)",
                                    "&:hover": {
                                      backgroundColor: "rgba(255, 152, 0, 0.2)",
                                    },
                                  }}
                                >
                                  <TimeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Slot">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteSlot(slot.id)}
                                  sx={{
                                    color: "#f87171",
                                    backgroundColor: "rgba(248, 113, 113, 0.1)",
                                    "&:hover": {
                                      backgroundColor: "rgba(248, 113, 113, 0.2)",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Box>
                        </Box>

                        <CardContent sx={{ p: 3 }}>
                          {/* Time Display */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                              p: 2,
                              mb: 2,
                              borderRadius: 2,
                              background: "linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)",
                              border: "1px solid #90caf9",
                            }}
                          >
                            <TimeIcon sx={{ color: "#1976d2" }} />
                            <Typography variant="h6" fontWeight={700} sx={{ color: "#1976d2" }}>
                              {slot.startTime} – {slot.endTime}
                            </Typography>
                          </Box>

                          {/* Restaurants */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                              Available Restaurants:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {slot.restaurants && slot.restaurants.length > 0 ? (
                                slot.restaurants.map((restaurant, idx) => (
                                  <Chip
                                    key={idx}
                                    label={restaurant}
                                    size="small"
                                    sx={{
                                      background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))
                              ) : (
                                <Chip
                                  label="No specific restaurants"
                                  size="small"
                                  sx={{
                                    background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Food Items */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                              Food Items:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {slot.foodItems && slot.foodItems.length > 0 ? (
                                slot.foodItems.map((item, idx) => (
                                  <Chip
                                    key={idx}
                                    label={item}
                                    size="small"
                                    sx={{
                                      background: "linear-gradient(135deg, #ff9800 0%, #ff5722 100%)",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))
                              ) : (
                                <Chip
                                  label="No specific food items"
                                  size="small"
                                  sx={{
                                    background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Item Options */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ mb: 1, color: "#64748b" }}>
                              Item Options:
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                              {slot.item_options && slot.item_options.length > 0 ? (
                                slot.item_options.map((option, idx) => (
                                  <Chip
                                    key={idx}
                                    label={option}
                                    size="small"
                                    sx={{
                                      background: "linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  />
                                ))
                              ) : (
                                <Chip
                                  label="No item options"
                                  size="small"
                                  sx={{
                                    background: "linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)",
                                    color: "#64748b",
                                    fontWeight: 600,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>

                          {/* Status */}
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Chip
                              label={slot.isActive ? "Active" : "Inactive"}
                              size="small"
                              sx={{
                                background: slot.isActive
                                  ? "linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)"
                                  : "linear-gradient(135deg, #f44336 0%, #ef5350 100%)",
                                color: "white",
                                fontWeight: 700,
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Edit Slot Dialog */}
        <Dialog
          open={editDialog.open}
          onClose={() => setEditDialog({ open: false, slot: null, isNew: false })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.3)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #2c3e50 0%, #34495e 100%)",
              color: "white",
              borderRadius: "16px 16px 0 0",
            }}
          >
            {editDialog.isNew ? "➕ Add New Slot" : "✏️ Edit Slot"}
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Slot Name</InputLabel>
                  <Select
                    value={editDialog.slot?.name || ""}
                    onChange={(e) => {
                      const selectedSlotName = e.target.value
                      const predefinedTimes = getPredefinedSlotTimes(selectedSlotName)

                      setEditDialog({
                        ...editDialog,
                        slot: {
                          ...editDialog.slot,
                          name: selectedSlotName,
                          startTime: predefinedTimes.startTime,
                          endTime: predefinedTimes.endTime,
                        },
                      })
                    }}
                    label="Slot Name"
                    sx={{ borderRadius: 2 }}
                  >
                    {PREDEFINED_SLOTS.map((slot) => (
                      <MenuItem key={slot.value} value={slot.value}>
                        {slot.emoji} {slot.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              {editDialog.slot?.name === "Custom" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Custom Slot Name"
                    value={editDialog.slot?.customName || ""}
                    onChange={(e) => setEditDialog({
                      ...editDialog,
                      slot: { ...editDialog.slot, customName: e.target.value }
                    })}
                    placeholder="e.g., Tea Time, Snacks, Evening Break"
                  />
                </Grid>
              )}
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start Time"
                  value={editDialog.slot?.startTime || ""}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    slot: { ...editDialog.slot, startTime: e.target.value }
                  })}
                  InputLabelProps={{ shrink: true }}
                  disabled={editDialog.slot?.name !== "Custom"}
                  helperText={editDialog.slot?.name === "Custom" ? "Set custom start time" : "Time is fixed for this slot type"}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  type="time"
                  label="End Time"
                  value={editDialog.slot?.endTime || ""}
                  onChange={(e) => setEditDialog({
                    ...editDialog,
                    slot: { ...editDialog.slot, endTime: e.target.value }
                  })}
                  InputLabelProps={{ shrink: true }}
                  disabled={editDialog.slot?.name !== "Custom"}
                  helperText={editDialog.slot?.name === "Custom" ? "Set custom end time" : "Time is fixed for this slot type"}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Restaurants (optional, comma separated)"
                  value={editDialog.slot?.restaurantText || editDialog.slot?.restaurants?.join(", ") || ""}
                  onChange={(e) => {
                    const restaurantText = e.target.value

                    setEditDialog({
                      ...editDialog,
                      slot: {
                        ...editDialog.slot,
                        restaurantText: restaurantText, // Store raw text
                        restaurants: restaurantText
                          .split(",")
                          .map((r) => r.trim())
                          .filter((r) => r.length > 0)
                      },
                    })
                  }}
                  placeholder="e.g., Resto A, Resto B, Resto C (optional)"
                  helperText="Enter restaurant names separated by commas. Leave empty if not applicable."
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                  Item Options (at least one required):
                </Typography>

                {/* Current Options */}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: "#64748b" }}>
                    Current Options:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                    {editDialog.slot?.item_options?.map((option, idx) => (
                      <Chip
                        key={idx}
                        label={option}
                        onDelete={() => handleRemoveItemOption(option)}
                        sx={{
                          background: "linear-gradient(135deg, #673ab7 0%, #9c27b0 100%)",
                          color: "white",
                          fontWeight: 600,
                          "& .MuiChip-deleteIcon": {
                            color: "rgba(255, 255, 255, 0.8)",
                            "&:hover": {
                              color: "white",
                            },
                          },
                        }}
                      />
                    )) || []}
                    {(!editDialog.slot?.item_options || editDialog.slot.item_options.length === 0) && (
                      <Typography variant="body2" sx={{ color: "#94a3b8", fontStyle: "italic" }}>
                        No options added yet
                      </Typography>
                    )}
                  </Box>
                </Box>



                {/* Add Custom Option */}
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, color: "#64748b" }}>
                    Add Custom Option:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <TextField
                      size="small"
                      placeholder="e.g., Ice Cream, Gluten-Free, etc."
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleAddCustomOption(e.target.value)
                          e.target.value = ""
                        }
                      }}
                      sx={{ flexGrow: 1 }}
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={(e) => {
                        const input = e.target.closest(".MuiBox-root").querySelector("input")
                        handleAddCustomOption(input.value)
                        input.value = ""
                      }}
                      sx={{
                        background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                        minWidth: "auto",
                        px: 2,
                      }}
                    >
                      Add
                    </Button>
                  </Box>
                  
                  {/* Veg/Non-Veg Toggle */}
                  {editDialog.slot?.item_options?.length > 0 && (
                    <Box sx={{ mt: 2, p: 2, border: "1px solid #e0e0e0", borderRadius: 2, background: "#f9f9f9" }}>
                      <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
                        Veg/Non-Veg Options:
                      </Typography>
                      {(() => {
                        // Get all unique base options (both single and from veg/non-veg variants)
                        const allBaseOptions = new Set()
                        
                        editDialog.slot.item_options.forEach(option => {
                          if (option.includes("(Veg)")) {
                            allBaseOptions.add(option.replace(" (Veg)", ""))
                          } else if (option.includes("(Non-Veg)")) {
                            allBaseOptions.add(option.replace(" (Non-Veg)", ""))
                          } else {
                            allBaseOptions.add(option)
                          }
                        })
                        
                        return Array.from(allBaseOptions).map((baseOption) => {
                          const hasVegNonVeg = editDialog.slot?.item_options?.includes(`${baseOption} (Veg)`) || editDialog.slot?.item_options?.includes(`${baseOption} (Non-Veg)`)
                          return (
                            <Box key={baseOption} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", py: 1 }}>
                              <Typography variant="body2">{baseOption}</Typography>
                              <Button
                                size="small"
                                variant={hasVegNonVeg ? "contained" : "outlined"}
                                onClick={() => {
                                  const currentOptions = editDialog.slot?.item_options || []
                                  if (hasVegNonVeg) {
                                    // Remove veg/non-veg variants and add back base option
                                    const newOptions = currentOptions.filter(opt => 
                                      opt !== `${baseOption} (Veg)` && opt !== `${baseOption} (Non-Veg)`
                                    )
                                    if (!newOptions.includes(baseOption)) {
                                      newOptions.push(baseOption)
                                    }
                                    setEditDialog({
                                      ...editDialog,
                                      slot: { ...editDialog.slot, item_options: newOptions }
                                    })
                                  } else {
                                    // Remove base option and add veg/non-veg variants
                                    const newOptions = currentOptions.filter(opt => opt !== baseOption)
                                    newOptions.push(`${baseOption} (Veg)`, `${baseOption} (Non-Veg)`)
                                    setEditDialog({
                                      ...editDialog,
                                      slot: { ...editDialog.slot, item_options: newOptions }
                                    })
                                  }
                                }}
                                sx={{ minWidth: 80, fontSize: "11px" }}
                              >
                                {hasVegNonVeg ? "Veg/Non-Veg" : "Single"}
                              </Button>
                            </Box>
                          )
                        })
                      })()}
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={() => setEditDialog({ open: false, slot: null, isNew: false })}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                Cancel
              </Button>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                onClick={handleSaveSlot}
                variant="contained"
                sx={{
                  borderRadius: 2,
                  fontWeight: 700,
                  background: "linear-gradient(135deg, #06d6a0 0%, #20d5d5 100%)",
                  boxShadow: "0 8px 25px rgba(6, 214, 160, 0.3)",
                }}
              >
                {editDialog.isNew ? "Add Slot" : "Update Slot"}
              </Button>
            </motion.div>
          </DialogActions>
        </Dialog>

        {/* Extend Slot Dialog */}
        <Dialog
          open={extendDialog.open}
          onClose={() => setExtendDialog({ open: false, slot: null, newEndTime: "" })}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 4,
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
              color: "white",
              borderRadius: "16px 16px 0 0",
            }}
          >
            ⏰ Extend Slot Time
          </DialogTitle>
          <DialogContent sx={{ py: 4 }}>
            {extendDialog.slot && (
              <>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                  {extendDialog.slot.name}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Current time: {extendDialog.slot.startTime} - {extendDialog.slot.endTime}
                </Typography>
                <TextField
                  fullWidth
                  type="time"
                  label="New End Time"
                  value={extendDialog.newEndTime}
                  onChange={(e) => setExtendDialog(prev => ({ ...prev, newEndTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ step: 600 }} // 10-minute steps
                  helperText="Extend in 10-minute increments (max 30 minutes extension)"
                />
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 2 }}>
            <Button
              onClick={() => setExtendDialog({ open: false, slot: null, newEndTime: "" })}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveExtension}
              variant="contained"
              sx={{
                borderRadius: 2,
                fontWeight: 700,
                background: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
              }}
            >
              Extend Slot
            </Button>
          </DialogActions>
        </Dialog>
      </motion.div>

      <style jsx>{`
        @keyframes rotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Container>
  )
}

export default AdminSlotManagement
